import React, { useState } from 'react';
import { ArrowLeft, Lightbulb, AlertTriangle } from 'lucide-react';

const BillingPage = ({
  subscription,
  loading,
  setCurrentView,
  handleSubscribe,
  handleCancelSubscription
}) => {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const onCancelSubscription = async () => {
    setCancelLoading(true);
    await handleCancelSubscription();
    setCancelLoading(false);
    setShowCancelConfirm(false);
  };

  return (
    <div className="billing-view">
      <div className="page-header">
        <h2>Subscription & Billing</h2>
        <button className="btn-back" onClick={() => setCurrentView('home')}><ArrowLeft size={16} /> Back</button>
      </div>

      {subscription?.plan_status === 'trialing' && (
        <div className="current-plan-card trial">
          <div className="plan-status">
            <span className="status-badge trialing">Trial</span>
            <h3>Pro Trial</h3>
          </div>
          <p>{subscription.trial_days_remaining} days remaining</p>
          <p className="trial-features">Full access to all Pro features</p>
        </div>
      )}

      {subscription?.plan_status === 'active' && (
        <div className="current-plan-card active">
          <div className="plan-status">
            <span className="status-badge active">Active</span>
            <h3>{subscription.plan_name} Plan</h3>
          </div>
          <p>£{subscription.price}/month</p>
        </div>
      )}

      {subscription?.plan_status === 'canceling' && (
        <div className="current-plan-card" style={{borderLeft: '4px solid #e67e22'}}>
          <div className="plan-status">
            <span className="status-badge" style={{background: '#e67e22'}}>Cancelling</span>
            <h3>{subscription.plan_name} Plan</h3>
          </div>
          <p style={{color: '#e67e22'}}>Your subscription will end on {subscription.cancel_at ? new Date(subscription.cancel_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'the end of your billing period'}.</p>
          <p>You retain full access until then.</p>
        </div>
      )}

      <div className="pricing-grid">
        <div className={`pricing-card ${subscription?.plan === 'starter' ? 'current' : ''}`}>
          <h3>Starter</h3>
          <div className="price">£9<span>/month</span></div>
          <ul className="features-list">
            <li>Up to 10 client profiles</li>
            <li>AI meal plan generation</li>
            <li>Basic macro targets</li>
            <li>2 meal swaps per plan</li>
            <li className="disabled">Quick Meal Plan</li>
            <li className="disabled">Meal plan templates</li>
            <li className="disabled">Custom branding</li>
            <li className="disabled">Shopping lists</li>
            <li className="disabled">Calorie cycling</li>
          </ul>
          <button 
            className="btn-secondary btn-full"
            onClick={() => handleSubscribe('starter')}
            disabled={loading || (subscription?.plan === 'starter' && subscription?.plan_status === 'active')}
          >
            {subscription?.plan === 'starter' && subscription?.plan_status === 'active' 
              ? 'Current Plan' 
              : subscription?.plan_status === 'active' 
                ? 'Downgrade to Starter' 
                : 'Choose Starter'}
          </button>
        </div>

        <div className={`pricing-card featured ${subscription?.plan === 'pro' ? 'current' : ''}`}>
          <div className="popular-badge">Most Popular</div>
          <h3>Pro</h3>
          <div className="price">£19<span>/month</span></div>
          <ul className="features-list">
            <li>Up to 50 client profiles</li>
            <li>AI meal plan generation</li>
            <li>Advanced macro targets</li>
            <li>Unlimited meal swaps</li>
            <li>Quick Meal Plan</li>
            <li>Custom branding</li>
            <li>Shopping lists</li>
            <li>Calorie cycling</li>
            <li>White-label PDFs</li>
            <li className="disabled">Meal plan templates</li>
          </ul>
          <button 
            className="btn-primary btn-full"
            onClick={() => handleSubscribe('pro')}
            disabled={loading || (subscription?.plan === 'pro' && subscription?.plan_status === 'active')}
          >
            {subscription?.plan === 'pro' && subscription?.plan_status === 'active' 
              ? 'Current Plan' 
              : subscription?.plan === 'unlimited' && subscription?.plan_status === 'active'
                ? 'Downgrade to Pro'
                : subscription?.plan === 'starter' && subscription?.plan_status === 'active'
                  ? 'Upgrade to Pro'
                  : 'Choose Pro'}
          </button>
        </div>

        <div className={`pricing-card ${subscription?.plan === 'unlimited' ? 'current' : ''}`}>
          <h3>Unlimited</h3>
          <div className="price">£49<span>/month</span></div>
          <ul className="features-list">
            <li><strong>Unlimited</strong> client profiles</li>
            <li>Everything in Pro</li>
            <li>Meal plan templates</li>
            <li>Up to 3 team members</li>
          </ul>
          <button 
            className="btn-secondary btn-full"
            onClick={() => handleSubscribe('unlimited')}
            disabled={loading || (subscription?.plan === 'unlimited' && subscription?.plan_status === 'active')}
          >
            {subscription?.plan === 'unlimited' && subscription?.plan_status === 'active' 
              ? 'Current Plan' 
              : subscription?.plan_status === 'active'
                ? 'Upgrade to Unlimited'
                : 'Choose Unlimited'}
          </button>
        </div>
      </div>
      
      {subscription?.plan_status === 'active' && (
        <p className="billing-note">
          <Lightbulb size={14} style={{marginRight: '6px'}} />When you change plans, you'll be charged the difference pro-rata for the remaining billing period.
        </p>
      )}

      {/* Cancel Subscription Section */}
      {(subscription?.plan_status === 'active' || subscription?.plan_status === 'trialing') && (
        <div className="cancel-section" data-testid="cancel-subscription-section">
          {!showCancelConfirm ? (
            <button 
              className="btn-cancel-sub"
              data-testid="cancel-subscription-btn"
              onClick={() => setShowCancelConfirm(true)}
            >
              Cancel Subscription
            </button>
          ) : (
            <div className="cancel-confirm-card">
              <AlertTriangle size={20} color="#e74c3c" />
              <h4>Are you sure you want to cancel?</h4>
              <p>Your subscription will remain active until the end of your current billing period. After that, you'll lose access to premium features.</p>
              <div className="cancel-actions">
                <button 
                  className="btn-danger"
                  data-testid="confirm-cancel-btn"
                  onClick={onCancelSubscription}
                  disabled={cancelLoading}
                >
                  {cancelLoading ? 'Cancelling...' : 'Yes, Cancel Subscription'}
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={cancelLoading}
                >
                  Keep My Plan
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BillingPage;
